import {
  type PlotlineProps,
  createHighchartsPlotLines,
} from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/plotline";
import type { I18n } from "@lingui/core";
import type { AxisOptions, XAxisOptions, YAxisOptions } from "highcharts";

export function getLocalizedDateFormats(locale: string): AxisOptions["dateTimeLabelFormats"] {
  const usLike = ["en-US", "en-CA"];
  const isUS = usLike.includes(locale);

  return {
    millisecond: { main: "%H:%M:%S.%L" },
    second: { main: "%H:%M:%S" },
    minute: { main: "%H:%M" },
    hour: { main: "%H:%M" },
    day: { main: isUS ? "%b %e" : "%e. %b" },
    week: { main: isUS ? "%b %e" : "%e. %b" },
    month: { main: "%b '%y" },
    year: { main: "%Y" },
  };
}

export const HighchartsXAxisBottomDefault = (plotlinePropsList: PlotlineProps[], i18n: I18n): XAxisOptions => {
  const plotLines = plotlinePropsList
    .flatMap((plotlineProp) => {
      return createHighchartsPlotLines(plotlineProp);
    })
    .flat();

  return {
    type: "datetime",
    dateTimeLabelFormats: getLocalizedDateFormats(i18n.locale),
    labels: {
      // Keep labels horizontal instead of rotating to -45° (the Highcharts 13
      // default when ticks are crowded). A numeric `rotation` makes Highcharts
      // use it as the only allowed angle, so auto-rotation never kicks in.
      rotation: 0,
    },
    plotLines: plotLines,
    plotBands: [],
    // Keep the fine (6h) grid ticks, but don't force a 3h major tick interval:
    // letting Highcharts pick the interval from `tickPixelInterval` shows only
    // as many labels as fit the available width, horizontally.
    minorTickInterval: 1000 * 60 * 60 * 6,
    crosshair: true,
  };
};

export const HighchartsXAxisTop: XAxisOptions = {
  linkedTo: 0,
  type: "datetime",
  tickInterval: 24 * 3600 * 1000,
  labels: {
    format: '{value:<span style="font-size: 12px; font-weight: bold">%a</span> %b %e}',
    align: "left",
    x: 3,
    y: 4,
  },
  opposite: true,
  tickLength: 20,
  gridLineWidth: 1,
};

const DefaultIconYAxis: YAxisOptions = {
  visible: false,
  title: {
    text: undefined,
  },
};

// We always set the chart to these 8 yAxis options
export const HighchartsYAxis: Array<YAxisOptions> = [
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: false,
  },
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: true,
  },
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: false,
  },
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: true,
  },
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: false,
  },
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: true,
  },
  {
    visible: true,
    title: {
      text: undefined,
    },
    opposite: false,
  },
  // last yaxis is reserved for icon (weather symbol and wind barbs)
  DefaultIconYAxis,
];

const DefaultPrecipitation1hYAxis: YAxisOptions = {
  visible: false,
  softMax: 10,
  // labels TODO: labels is place above column (could maybe set on series)
  title: {
    text: undefined,
  },
  opposite: false,
};

const DefaultMslPressureYAxis: YAxisOptions = {
  visible: true,
  min: 950,
  max: 1030,
  // min max could also not be set as long series point to yAxis idx without other series data on it.
  title: {
    text: undefined,
  },
  opposite: true,
};
const DefaultSunDurationYAxis: YAxisOptions = {
  visible: false,
  min: 0,
  max: 60, // minutes
  endOnTick: false,
  title: {
    text: undefined,
  },
  opposite: false,
};
const DefaultGlobalRadYAxis: YAxisOptions = {
  visible: false,
  title: {
    text: undefined,
  },
  opposite: false,
};

const DefaultCloudAreaFraction: YAxisOptions = {
  visible: false,
  min: 0,
  max: 100, // percent
  reversed: true,
  endOnTick: false,
  title: {
    text: undefined,
  },
};

/**
 * yAxis list with some preset of yAxis
 *
 * Note: Remember that we also want to implement a function for the popup plot
 * Checks the unit of each parameter and if they are different they should get different Y-axes.
 * So question is which slots should we reserve for what.
 * */
export const HighchartsYAxisPointForecast: Array<YAxisOptions> = [
  HighchartsYAxis[0], //temperature
  DefaultMslPressureYAxis,
  DefaultPrecipitation1hYAxis,
  DefaultGlobalRadYAxis,
  DefaultSunDurationYAxis,
  DefaultCloudAreaFraction,
  HighchartsYAxis[6],
  DefaultIconYAxis,
];
